import React, { useEffect, useMemo, useState } from "react";

import PanelTemplate from "../../templates/Panel";
import Table from "../../components/Table";
import Box from "../../components/Box";
import Button from "../../components/Button";
import PageHeader from "../../components/PageHeader";
import FilterSimple from "../../components/FilterSimple";
import Pagination from "../../components/Pagination";
import { ModalDelete } from "../../components/ModalDelete";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { toast } from "react-toastify";

const pageSearchParams = [
    { name: 'Título', key: '[title]', operation: 'contain', type: 'text' },
    { name: 'Slug', key: '[slug]', operation: 'contain', type: 'text' },
];

const getPageSearchParam = field => (
    pageSearchParams.find(param => param.key === field) || pageSearchParams[0]
);

const normalizePage = page => {
    const parsedPage = parseInt(page, 10);
    return Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;
};

const parsePageListFilters = search => {
    const params = new URLSearchParams(search);
    const searchParam = getPageSearchParam(params.get('search_field'));

    return {
        page: normalizePage(params.get('page')),
        searchField: searchParam.key,
        searchOperation: params.get('search_operation') || searchParam.operation || 'contain',
        search: params.get('search') || '',
    };
};

const buildPageApiQuery = filters => {
    const params = new URLSearchParams();

    if (filters.search.trim() !== '') {
        params.append(`filters[0]${filters.searchField}[${filters.searchOperation}]`, filters.search.trim());
    }

    params.set('page', filters.page);

    return params.toString();
};

const buildPageListSearch = filters => {
    const params = new URLSearchParams();

    if (filters.page > 1) params.set('page', filters.page);
    if (filters.search.trim() !== '') {
        params.set('search_field', filters.searchField);
        params.set('search_operation', filters.searchOperation);
        params.set('search', filters.search.trim());
    }

    return params.toString();
};

const PageList = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [pages, setPages] = useState([]);
    const [data, setData] = useState({ header: [], rows: [] });

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const [selectedIds, setSelectedIds] = useState([]);
    const [showConfirmMultipleDelete, setShowConfirmMultipleDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const pageFilters = useMemo(() => parsePageListFilters(location.search), [location.search]);

    const updatePageListUrl = (changes, resetPage = true) => {
        const nextFilters = {
            ...pageFilters,
            ...changes,
            page: resetPage ? 1 : (changes.page || pageFilters.page),
        };
        const search = buildPageListSearch(nextFilters);

        navigate({
            pathname: location.pathname,
            search: search ? `?${search}` : '',
        });
    }

    useEffect(() => {
        getPages();
    }, [location.search]);

    useEffect(() => {
        generateData();
    }, [pages]);

    const getPages = () => {
        setLoading(true);

        api.get(`/page/paginate?${buildPageApiQuery(pageFilters)}`).then(res => {
            setPages(res.data.resources.data);
            setCurrentPage(res.data.resources.current_page);
            setLastPage(res.data.resources.last_page);
            setPerPage(res.data.resources.per_page);
            setTotalResults(res.data.resources.total);
            setHasNextPage(res.data.resources.next_page_url !== null);
            setHasPrevPage(res.data.resources.prev_page_url !== null);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const generateData = () => {
        const header = ['Título', 'Slug'];
        const rows = [];

        pages.map(page => {
            rows.push({
                data: [
                    page.title,
                    page.slug
                ],
                // Páginas padrão não recebem id na linha: o componente Table já
                // ignora id === null na seleção, então elas nunca entram na
                // exclusão em massa sem precisar de lógica extra no componente.
                id: page.is_default ? null : page.id,
                to: `/page/edit/${page.id}`
            })
        });

        setData({ header, rows });
    }

    const handleSetFilter = (value, metadata) => {
        updatePageListUrl({
            searchField: metadata?.field || pageFilters.searchField,
            searchOperation: metadata?.operation || pageFilters.searchOperation,
            search: metadata?.value ?? '',
        });
    }

    const handleSetPage = page => {
        updatePageListUrl({ page }, false);
    }

    const deleteMultiplePages = () => {
        setDeleting(true);

        api.delete(`/page/multiple`, {
            data: { ids: selectedIds }
        }).then(res => {
            setSelectedIds([]);
            setShowConfirmMultipleDelete(false);
            getPages();

            if (res.data?.skipped > 0) {
                toast.info(`${res.data.skipped} página(s) padrão do sistema não ${res.data.skipped > 1 ? 'foram excluídas' : 'foi excluída'} (protegida${res.data.skipped > 1 ? 's' : ''} contra exclusão).`);
            } else {
                toast.success('Páginas excluídas com sucesso');
            }
        }).catch(error => {
            renderError(error);
        }).then(() => setDeleting(false));
    }

    return (
        <>
            <ModalDelete
                show={showConfirmMultipleDelete}
                text={<p>Tem certeza que deseja excluir <strong>{selectedIds.length} {selectedIds.length > 1 ? 'páginas' : 'página'}</strong>?</p>}
                action={deleteMultiplePages}
                loading={deleting}
                close={() => setShowConfirmMultipleDelete(false)}
            />
            <PanelTemplate id="page-list">
                <PageHeader
                    left={
                        <FilterSimple
                            params={pageSearchParams}
                            filter={handleSetFilter}
                            externalValue={pageFilters.search}
                            externalOption={pageFilters.searchField}
                        />
                    }
                    right={
                        <>
                            {selectedIds.length > 0 &&
                                <Button
                                    type={`secondary`}
                                    size={`small`}
                                    svg={`delete`}
                                    text={`Excluir ${selectedIds.length} selecionados`}
                                    action={() => setShowConfirmMultipleDelete(true)}
                                />
                            }
                            <Button
                                type={`primary`}
                                size={`small`}
                                text={`Criar página`}
                                svg="plus-white"
                                action={() => navigate('/page/create')}
                            />
                        </>
                    }
                />
                <Box className={`no-padding with-pagination`}>
                    <Table
                        header={data.header}
                        rows={data.rows}
                        loading={loading}
                        checks={true}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                    />
                </Box>
                {!loading &&
                    <Pagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        perPage={perPage}
                        totalResults={totalResults}
                        hasNextPage={hasNextPage}
                        hasPrevPage={hasPrevPage}
                        setCurrentPage={handleSetPage}
                    />
                }
            </PanelTemplate>
        </>
    );
}

export default PageList;
